'use client';

import { ViolationError } from '@app/lib/form/contracts/ViolationError';
import { useShowErrorState } from '@app/lib/form/hooks/useShowErrorState';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { useTranslations } from 'next-intl';
import { ChangeEvent, ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';

interface Props {
    name: string;
    id?: string;
    label?: ReactNode;
    defaultChecked?: boolean;
    value?: string;
    form?: string;
    error?: ViolationError;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    checked?: boolean;
}

export function CheckboxInput({
    name,
    id,
    label,
    defaultChecked,
    form,
    error,
    onChange,
    value,
    disabled = false,
    checked,
}: Props) {
    const t = useTranslations();

    const [showError, handleChange] = useShowErrorState(error, onChange);

    const inputId = id ?? `checkbox-${name}`;

    return (
        <div className="flex flex-col gap-2">
            <label
                htmlFor={inputId}
                className="flex items-start gap-2 rounded outline-offset-2 has-[input:focus-visible]:outline">
                <div
                    className={twJoin(
                        'flex size-5 items-center justify-center rounded border border-neutral-400 text-transparent',
                        'has-[:checked]:border-primary-500 has-[:checked]:bg-primary-500 has-[:checked]:text-white',
                    )}>
                    <input
                        type="checkbox"
                        id={inputId}
                        name={name}
                        form={form}
                        disabled={disabled}
                        className="absolute min-w-0 appearance-none"
                        value={value ? value : 'true'}
                        defaultChecked={defaultChecked}
                        checked={checked}
                        onChange={handleChange}
                    />

                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M5.91308 9.03516H5.91326C5.99256 9.03525 6.07081 9.05331 6.14213 9.08798C6.21341 9.12262 6.27591 9.17295 6.32497 9.2352L5.91308 9.03516ZM5.91308 9.03516H5.09394C4.89031 9.03516 4.7766 9.26975 4.90232 9.4292L4.90237 9.42927L8.11214 13.4957L8.11218 13.4957C8.32247 13.7619 8.72568 13.7611 8.93667 13.4962L8.93708 13.4957L14.6597 6.24398C14.6598 6.24378 14.66 6.24358 14.6602 6.24338C14.7884 6.08295 14.6699 5.85 14.4689 5.85H13.6498C13.4894 5.85 13.3367 5.9235 13.2376 6.05044C13.2375 6.05055 13.2374 6.05066 13.2373 6.05077L8.52403 12.0216M5.91308 9.03516L8.52403 12.0216M8.52403 12.0216L6.32506 9.23531L8.52403 12.0216Z"
                            fill="currentColor"
                            stroke="currentColor"
                            strokeWidth="0.3"
                        />
                    </svg>
                </div>
                <span>{label != null && label}</span>
            </label>

            {showError && isViolationMessage(error) && <div className="text-sm text-red-500">{t(error.message)}</div>}
        </div>
    );
}
